import { computed } from "mobx";

import { StreamClient } from "@formant/core/client/StreamClient";
import { defined } from "@formant/core/common/defined";
import { distinct } from "@formant/core/common/distinct";
import { DefinedEntity } from "@formant/core/model/DefinedEntity";
import { IStream } from "@formant/core/model/IStream";
import { StreamType } from "@formant/core/model/StreamType";
import { Uuid } from "@formant/core/model/Uuid";

import { IAuthStore } from "./IAuthStore";
import { IStreamStore, StreamFilter } from "./IStreamStore";
import { StoreCache } from "./StoreCache";

export class StreamStore implements IStreamStore {
    private listCache = new StoreCache<undefined, DefinedEntity<IStream>[]>();
    private cache = new StoreCache<Uuid, DefinedEntity<IStream> | false>();

    constructor(
        private streamClient: StreamClient,
        private authStore: IAuthStore,
    ) {}

    public list(filter?: StreamFilter): DefinedEntity<IStream>[] | undefined {
        let result = this.listAll()?.filter(_ => _.enabled);
        if (result && filter) {
            result = result.filter(
                _ =>
                    (filter === "telemetry" && _.isTelemetryFilter) ||
                    (filter === "event" && _.isEventFilter),
            );
        }
        return result;
    }

    @computed
    public get streamNameTypeMap(): Map<string, StreamType> | undefined {
        return this.list()?.reduce(
            (a, _) => a.set(_.streamName, _.streamType),
            new Map<string, StreamType>(),
        );
    }

    @computed
    public get streamNameAliasOrNameMap(): Map<string, string> | undefined {
        return this.list()?.reduce(
            (a, _) => a.set(_.streamName, _.alias || _.streamName),
            new Map<string, string>(),
        );
    }

    public getStreamNames(filter?: StreamFilter): string[] | undefined {
        return this.list(filter)?.map(_ => _.streamName);
    }

    public getStreamTypes(filter?: StreamFilter): StreamType[] | undefined {
        const streamTypes = this.list(filter)?.map(_ => _.streamType);
        return streamTypes && distinct(streamTypes);
    }

    public listAll(): DefinedEntity<IStream>[] | undefined {
        return this.listCache.get(
            undefined,
            async () => await this.fetchList(),
        );
    }

    public async listAllAsync(): Promise<DefinedEntity<IStream>[]> {
        return this.listCache.getAsync(
            undefined,
            async () => await this.fetchList(),
        );
    }

    public get(
        id: Uuid,
    ): DefinedEntity<DefinedEntity<IStream>> | false | undefined {
        return this.cache.get(
            id,
            async () => (await this.fetchGet(id)) || false,
        );
    }

    public async create(stream: IStream): Promise<DefinedEntity<IStream>> {
        const created = await this.streamClient.create(
            await this.authStore.token(),
            stream,
        );
        this.cache.set(defined(created.id), created);
        this.listCache.clear();
        return created;
    }

    public async update(id: Uuid, stream: Partial<IStream>) {
        const updated = await this.streamClient.update(
            await this.authStore.token(),
            id,
            stream,
        );
        this.cache.set(id, updated);
        this.listCache.clear();
        return updated;
    }

    public save(stream: IStream): Promise<DefinedEntity<IStream>> {
        const { id } = stream;
        if (id) {
            return this.update(id, stream);
        } else {
            return this.create(stream);
        }
    }

    public async disable(id: Uuid) {
        return await this.update(id, {
            isOverviewColumn: false,
            isOverviewRow: false,
            enabled: false,
        });
    }

    private async fetchGet(id: Uuid): Promise<DefinedEntity<IStream> | false> {
        const stream = await this.streamClient.get(
            await this.authStore.token(),
            id,
        );
        const value = stream || false;
        this.cache.set(id, value);
        return value;
    }

    private async fetchList(): Promise<DefinedEntity<IStream>[]> {
        const value = await this.streamClient.list(
            await this.authStore.token(),
        );
        value.forEach(_ => this.cache.set(defined(_.id), _));
        this.listCache.set(undefined, value);
        return value;
    }
}
